import { render, h } from "preact";
import { useRef, useEffect, useState, useCallback } from "preact/hooks";
import {
    Layer,
    Map,
    Source,
    Popup,
    NavigationControl,
    Marker,
} from "react-map-gl";
import "./preact-map.component.scss";
import { quietenMapLabels } from "../../../assets/js/lib/content/map-style";
import {
    MapMarker,
    REVISIT_OFFSET,
    getStopZIndex,
    toStops,
} from "../map-marker/map-marker.jsx";

const SOURCE_ID = "points";
const CLUSTERS_ID = "clusters";
const UNCLUSTERED_ID = "unclustered-point";

// Wat de clusterbron te zien krijgt zolang er een reisroute op de kaart staat: die route tekent
// zijn eigen markers, dus Mapbox moet er geen tweede stel bollen overheen leggen.
const EMPTY_DATA = { type: "FeatureCollection", features: [] };

// Gentle ease so camera moves glide instead of snapping.
const easeInOutCubic = (t) =>
    t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

function PreactMap() {
    const config = window.__map_config;
    const mapRef = useRef();
    const [popup, setPopup] = useState(null);
    const [points, setPoints] = useState(config.points || []);
    const [itineraryPoints, setItineraryPoints] = useState([]);

    const hasItinerary = itineraryPoints.length > 0;
    // De route van de reis waar de bezoeker overheen hangt, in stops met hun eerste dag erin:
    // dezelfde markers als op de reispagina zelf.
    const itineraryStops = toStops(itineraryPoints);

    const itineraryLineData = {
        type: "Feature",
        geometry: {
            type: "LineString",
            coordinates: getCoordinates(itineraryPoints),
        },
    };

    const data = {
        type: "FeatureCollection",
        features: points.map((point) => ({
            type: "Feature",
            id: point.id,
            properties: point,
            geometry: {
                type: "Point",
                coordinates: [point.longitude, point.latitude],
            },
        })),
    };

    function getCoordinates(points) {
        return points.map((point) => [point.longitude, point.latitude]);
    }

    function getBounds(points) {
        if (points.length <= 0) {
            return [
                [-90, 90],
                [90, -90],
            ];
        }

        const coordinates = getCoordinates(points);

        const longtitudes = coordinates.map((point) => point[0]);
        const latitudes = coordinates.map((point) => point[1]);

        return [
            [Math.min(...longtitudes), Math.min(...latitudes)],
            [Math.max(...longtitudes), Math.max(...latitudes)],
        ];
    }

    /**
     * The map supports a 'global' view with points, and specific itineraryPoints which mark a track or itinerary
     */
    function onMapUpdateItineraryPoints(event) {
        const newItineraryPoints = event.detail.itineraryPoints;

        if (!newItineraryPoints || !Array.isArray(newItineraryPoints)) {
            throw new Error("Invalid itinerary points provided");
        }

        setItineraryPoints(newItineraryPoints);
        // Slower transition when gliding between trips on hover.
        fitBounds(getBounds(newItineraryPoints), {
            maxZoom: 12,
            duration: 1200,
        });
    }

    /**
     * Clear itinerary points
     */
    function onMapClearItineraryPoints() {
        setItineraryPoints([]);
    }

    /** @param {Event & { detail: { points?: Point[] }}} event */
    function onMapUpdatePoints(event) {
        const newPoints = event.detail.points;

        if (!newPoints || !Array.isArray(newPoints)) {
            throw new Error("Invalid points provided");
        }

        setPoints(newPoints);
        fitBounds(getBounds(newPoints));
    }

    function onMapReset() {
        fitBounds(getBounds(points));
    }

    /** @param {Event & { detail: { id?: string }}} event */
    function onMapFocusPoint(event) {
        const pointId = event.detail.id;

        if (!pointId) {
            throw new Error("Invalid id provided");
        }

        const point = points.find((point) => point.id == pointId);

        if (!point) {
            throw new Error(`Point with id '${pointId}' not found`);
        }

        flyToPoint(point);
        setPopup({ ...point, time: new Date().getTime() });
    }

    function onMapClosePoint() {
        fitBounds(getBounds(points));
        setPopup(null);
    }

    function isMapReady() {
        if (!mapRef.current) return false;

        const container = mapRef.current.getContainer();
        return container && container.offsetWidth > 0 && container.offsetHeight > 0;
    }

    function flyToPoint(point) {
        if (!isMapReady()) return;

        mapRef.current.flyTo({
            center: [point.longitude, point.latitude],
            zoom: config.options.focusZoomLevel,
            duration: 1000,
            essential: true, // This respects prefers-reduced-motion
            curve: 1.42,
            easing: easeInOutCubic,
        });
    }

    function getResponsivePadding() {
        const container = mapRef.current.getContainer();
        const minDimension = Math.min(container.offsetWidth, container.offsetHeight);

        return Math.floor(minDimension * 0.22);
    }

    function fitBounds(bounds, overrides = {}) {
        if (!isMapReady()) return;

        const options = {
            ...config.mapOptions?.fitBoundsOptions,
            padding: getResponsivePadding(),
            duration: 800,
            essential: true,
            easing: easeInOutCubic,
            ...overrides,
        };

        mapRef.current.fitBounds(bounds, options);
    }

    useEffect(() => {
        window.addEventListener("map:update-points", onMapUpdatePoints);
        window.addEventListener("map:reset", onMapReset);
        window.addEventListener("map:focus-point", onMapFocusPoint);
        window.addEventListener("map:close-point", onMapClosePoint);
        window.addEventListener(
            "map:update-itinerary-points",
            onMapUpdateItineraryPoints,
        );
        window.addEventListener(
            "map:clear-itinerary-points",
            onMapClearItineraryPoints,
        );

        return () => {
            window.removeEventListener("map:update-points", onMapUpdatePoints);
            window.removeEventListener("map:reset", onMapReset);
            window.removeEventListener("map:focus-point", onMapFocusPoint);
            window.removeEventListener("map:close-point", onMapClosePoint);
            window.removeEventListener(
                "map:update-itinerary-points",
                onMapUpdateItineraryPoints,
            );
            window.removeEventListener(
                "map:clear-itinerary-points",
                onMapClearItineraryPoints,
            );
        };
    }, [points, itineraryPoints]);

    const onMapLoad = useCallback((event) => {
        const map = event.target;

        if (!map) {
            return;
        }

        // Declutter the basemap: keep only country/island/capital labels.
        quietenMapLabels(map);

        map.on("click", CLUSTERS_ID, (event) => {
            const features = map.queryRenderedFeatures(event.point, {
                layers: [CLUSTERS_ID],
            });

            const feature = features[0];

            const clusterId = feature.properties.cluster_id;

            map.getSource(SOURCE_ID).getClusterExpansionZoom(
                clusterId,
                (error, zoom) => {
                    if (error) {
                        return error;
                    }

                    map.easeTo({
                        center: feature.geometry.coordinates,
                        zoom,
                    });
                },
            );
        });

        map.on("mouseenter", CLUSTERS_ID, () => {
            map.getCanvas().style.cursor = "pointer";
        });

        map.on("mouseleave", CLUSTERS_ID, () => {
            map.getCanvas().style.cursor = "";
        });

        // Let other components know that the map is ready for usage
        dispatchEvent(new CustomEvent("map:ready"));
    }, []);

    return (
        <Map
            {...config.mapOptions}
            initialViewState={{
                bounds: points.length > 0 ? getBounds(points) : undefined,
                ...config.mapOptions?.initialViewState,
            }}
            interactiveLayerIds={[CLUSTERS_ID]}
            ref={mapRef}
            onLoad={onMapLoad}
            locale={{
                "ScrollZoomBlocker.CtrlMessage":
                    "Gebruik ctrl + scroll om in te zoomen",
                "ScrollZoomBlocker.CmdMessage":
                    "Gebruik ⌘ + scroll om in te zoomen",
                "TouchPanBlocker.Message":
                    "Gebruik twee vingers om de kaart te verplaatsen",
            }}
        >
            {hasItinerary && (
                <Source id="itinerary" type="geojson" data={itineraryLineData}>
                    <Layer
                        id="itinerary"
                        type="line"
                        paint={{
                            "line-color": "#083941",
                            "line-width": 3,
                            "line-opacity": 0.4,
                        }}
                    />
                </Source>
            )}
            <NavigationControl showCompass={false} />
            <Source
                {...config.clusterOptions}
                id={SOURCE_ID}
                type="geojson"
                data={hasItinerary ? EMPTY_DATA : data}
            >
                <Layer
                    id={CLUSTERS_ID}
                    type="circle"
                    source={SOURCE_ID}
                    filter={["has", "point_count"]}
                    paint={{
                        "circle-color": [
                            "step",
                            ["get", "point_count"],

                            // with three steps to implement three types of circles:

                            "#083941", // default

                            10, // step count
                            "#083941", // step color

                            25, // step count
                            "#083941", // max step color
                        ],
                        "circle-radius": [
                            "step",
                            ["get", "point_count"],

                            // TODO: @Aron Update sizes
                            15, // default radius

                            5, // step count
                            20, // step radius

                            10, //step count
                            25, // max step radius
                        ],
                        "circle-stroke-width": 6,
                        "circle-stroke-opacity": 0.6,
                        "circle-stroke-color": "#ffffff",
                    }}
                />
                <Layer
                    id="cluster-count"
                    type="symbol"
                    source={SOURCE_ID}
                    filter={["has", "point_count"]}
                    layout={{
                        "text-field": "{point_count_abbreviated}",
                        "text-size": 15,
                        "text-font": [
                            "DIN Pro Medium",
                            "Arial Unicode MS Bold",
                        ],
                    }}
                    paint={{
                        "text-color": "#ffffff", // change it as per your requirement
                    }}
                />
            </Source>

            {/* De route van de aangewezen reis: per stop zijn volgnummer. */}
            {hasItinerary &&
                itineraryStops.map((stop) => (
                    <Marker
                        key={stop.id}
                        longitude={stop.longitude}
                        latitude={stop.latitude}
                        anchor="center"
                        offset={[stop.revisit * REVISIT_OFFSET, 0]}
                        style={{ zIndex: getStopZIndex(stop, itineraryStops) }}
                    >
                        <MapMarker label={stop.firstDay} />
                    </Marker>
                ))}

            {!hasItinerary &&
                points.map((point) => (
                    <Marker
                        key={point.id}
                        longitude={point.longitude}
                        latitude={point.latitude}
                        anchor="center"
                        onClick={(event) => {
                            event.originalEvent.stopPropagation();
                            flyToPoint(point);
                            dispatchEvent(
                                new CustomEvent("map:point-click", {
                                    detail: { event, point },
                                }),
                            );
                            setPopup({ ...point, time: new Date().getTime() });
                        }}
                        style={{ cursor: "pointer" }}
                    >
                        <MapMarker />
                    </Marker>
                ))}

            {!!popup && (
                <Popup
                    key={`${popup.id}-${popup.time}`}
                    latitude={popup.latitude}
                    longitude={popup.longitude}
                    onClose={() => {
                        dispatchEvent(new CustomEvent("map:point-close"));
                        setPopup(null);
                    }}
                    focusAfterOpen={false} // This prevents the page from being scrolled to the top
                    anchor="bottom"
                    offset={18}
                    className="sb_map-popup"
                >
                    <div
                        class="sb_map-popup_title"
                        dangerouslySetInnerHTML={{ __html: popup.name }}
                    ></div>

                    {popup.url && (
                        <a
                            href={popup.url}
                            class="sb_map-popup_link sb_btn"
                            data-size="m"
                            data-variant="primary"
                        >
                            Lees meer
                        </a>
                    )}
                </Popup>
            )}
        </Map>
    );
}

//
// Check if the map should be rendered
//

window.__map_initialized = false;
window.__map_visibility_watched = false;

function checkSbPreactMap() {
    const container = document.querySelector(".sb_preact-map");

    if (!container || window.__map_initialized) {
        return;
    }

    // Don't initialize if the container is not visible (e.g., hidden on mobile)
    if (container.offsetParent === null) {
        return;
    }

    window.__map_initialized = true;

    // The init button is only a manual trigger; once the map is initializing
    // it has no function anymore, so hide it without waiting for Mapbox's
    // load event (which never fires when Mapbox is blocked).
    const initButton = container.querySelector("[data-init-map]");
    if (initButton) {
        initButton.style.display = "none";
    }

    render(<PreactMap />, container);
}

function destroySbPreactMap() {
    if (!window.__map_initialized) {
        return;
    }

    const container = document.querySelector(".sb_preact-map");

    if (!container) {
        return;
    }

    render(null, container);
    window.__map_initialized = false;
}

function watchMapVisibility() {
    if (window.__map_visibility_watched) {
        return;
    }

    window.__map_visibility_watched = true;

    window.addEventListener("resize", () => {
        const container = document.querySelector(".sb_preact-map");

        if (!container) {
            return;
        }

        const isVisible = container.offsetParent !== null;

        if (!isVisible && window.__map_initialized) {
            destroySbPreactMap();
        } else if (isVisible && !window.__map_initialized) {
            checkSbPreactMap();
        }
    });
}

window.addEventListener("map:init", () => {
    checkSbPreactMap();
    watchMapVisibility();
});
